import { useMemo } from 'react'
import * as React from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import Pane from 'components/Common/Pane'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import TriplePointTakeoverBackground from './TriplePointTakeoverBackground'
import LoyaltyTierPill from 'loyalty/components/LoyaltyTierPill'
import TriplePointTakeoverLogo from './TriplePointTakeoverLogo'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { triplePointCampaignId } from 'takeovers/societyTriplePointsTakeover/societyTriplePointsTakeoverConfig'
import TextLink from 'LuxKit/TextLink'

const SiteTakeoverBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
`

const CarouselContainer = styled(Pane)`
  position: relative;
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};

  ${mediaQueryUp.desktop} {
    /**
     * This adds room for the search section on all homepages
     */
    padding-bottom: ${rem(100)};

  }
`

const Span = styled.div`
  display: inline-grid;
  grid-template: 1ex / auto;
  place-items: center;
  vertical-align: middle;
  overflow: visible;
`

const Content = styled(VerticalSpacer)`
  position: relative;
  z-index: 1;
`

interface Props {
  className?: string
  viewOffersTo?: string
}

function TriplePointTakeoverHeroCarousel(props: Props) {
  const { className, viewOffersTo } = props

  const filters = useMemo<App.OfferListFilters>(() => {
    return {
      offerTypes: ['hotel', 'tactical_ao_hotel', 'tour_v2', 'cruise'],
      limit: 20,
      campaigns: [triplePointCampaignId],
      sortBy: 'triple-points',
    }
  }, [])

  const [tier] = useCurrentLoyaltyTier()
  const intl = useIntl()

  const triplePointsPill = (
    <Span>
      <LoyaltyTierPill kind="primary" tier={tier}>
        {intl.formatMessage({
          id: '3irLpPPES2F5XYJLAZ6C',
          defaultMessage: 'Triple Points',
          description: 'Loyalty tier pill label for triple points promotion',
        })}
      </LoyaltyTierPill>
    </Span>
  )

  return (
    <CarouselContainer className={className}>
      <SiteTakeoverBackground>
        <TriplePointTakeoverBackground background="hero" />
      </SiteTakeoverBackground>
      <Content gap={24}>
        <LayoutContainer>
          <Group direction="horizontal" gap={20} verticalAlign="center">
            <TriplePointTakeoverLogo size="medium" />
            <VerticalSpacer gap={2}>
              <Heading variant="heading2">
                <FormattedMessage
                  id="IWJ9IQpEnsYW66vMerdJ"
                  defaultMessage="Your next trip just got <i>3x</i> better"
                  description="Hero carousel heading for triple points takeover in the site takeover promotion"
                  values={{ i: (chunks) => <i>{chunks}</i> }}
                />
              </Heading>
              <BodyText variant="large">
                {viewOffersTo && <FormattedMessage
                  id="W8WrGpgsTZKAzCrbUN4E"
                  defaultMessage="Look for {pill} on select packages! Hurry, limited time only. <link>View all offers</link>"
                  description="Body text directing users to look for triple points pill with view all offers link"
                  values={{
                    pill: triplePointsPill,
                    link: (chunks: React.ReactNode) => <TextLink to={viewOffersTo}>{chunks}</TextLink>,
                  }}
                />}
                {!viewOffersTo && <FormattedMessage
                  id="U7jNG6JIZ4Q4z3Fh7-Vd"
                  defaultMessage="Look for {pill} on select packages! Hurry, limited time only."
                  description="Body text directing users to look for triple points pill on select packages"
                  values={{ pill: triplePointsPill }}
                />}
              </BodyText>
            </VerticalSpacer>
          </Group>
        </LayoutContainer>
        <OfferListCarousel
          filters={filters}
          tileStyle="card-default"
          tileSize="medium"
        />
      </Content>
    </CarouselContainer>
  )
}

export default React.memo(TriplePointTakeoverHeroCarousel)
